/**
 * P5 管理控制台 — 统一管理 AI / 业务规则 / 财务 / 通知 / 测试
 *
 * URL: /admin/control-panel?tab=ai|business|finance|data|code|test
 * 视觉：飞书风格（圆角 8、卡片色 #F5F6F7、间距 24px、行高 1.6）
 */
import { useEffect, useMemo, useState } from 'react';
import { Input, message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, Btn, Pill } from '../../components/ui';
import { fmtDateTime } from '../../lib/format';
import {
  ADMIN_CONFIG_LABELS, GROUP_META, type ConfigGroup, type ConfigLabel, findLabel,
} from '../../i18n/admin-config-labels';
import { AiProvidersSection } from './AiProviders';
// v1.2-rc2 ⑥：企微会话存档 tab
import { WeworkArchivePage } from './WeworkArchive';
// v1.5 阶段 5-B：RBAC 权限管理 tab（admin 自定义角色权限/用户级覆盖）
import { RbacAdmin } from './RbacAdmin';
import { useHasPermission } from '../../lib/permissions';
// v1.5 2FA UI 化：SMS 配置 + 2FA 设置
import { SmsConfig } from './SmsConfig';
import { Auth2faConfig } from './Auth2faConfig';
// v1.5 P0 B6：系统监控
import { SystemMonitor } from './SystemMonitor';
// B1-cfg：法大大电子签配置
import { FadadaConfig } from './FadadaConfig';
// 薪酬阶段1：班型定死价 / 职级底薪 / 提成矩阵配置
import { SalaryConfig } from './SalaryConfig';

const IS_DEV = (import.meta as any).env?.DEV === true;

export function ControlPanelPage() {
  const [params, setParams] = useSearchParams();
  const initial = (params.get('tab') as ConfigGroup) ?? 'ai';
  const [tab, setTab] = useState<ConfigGroup>(initial);

  useEffect(() => {
    setParams((p) => { p.set('tab', tab); return p; }, { replace: true });
  }, [tab]);

  const { data: lifecycleRows } = useQuery<any[]>({
    queryKey: ['lifecycle-config'],
    queryFn: () => $.get('/lifecycle-config'),
    staleTime: 30_000,
  });
  const { data: systemRows } = useQuery<any[]>({
    queryKey: ['system-configs'],
    queryFn: () => $.get('/admin/system-configs'),
    staleTime: 30_000,
  });
  const { data: aiConfigs } = useQuery<any[]>({
    queryKey: ['ai-configs'],
    queryFn: () => $.get('/ai/configs'),
    staleTime: 30_000,
  });

  // 合并：构建 key → 当前值映射
  const valueMap = useMemo(() => {
    const m = new Map<string, string>();
    (lifecycleRows ?? []).forEach((r: any) => m.set(r.configKey, r.configValue));
    (systemRows ?? []).forEach((r: any) => m.set(r.configKey, String(r.configValue ?? '')));
    return m;
  }, [lifecycleRows, systemRows]);

  // 最近修改时间
  const latestUpdate = useMemo(() => {
    const all: Array<{ key: string; updated_at: string }> = [];
    (lifecycleRows ?? []).forEach((r: any) => all.push({ key: r.configKey, updated_at: r.updatedAt }));
    (systemRows ?? []).forEach((r: any) => all.push({ key: r.configKey, updated_at: r.updatedAt }));
    if (all.length === 0) return null;
    return all.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0];
  }, [lifecycleRows, systemRows]);

  // v1.5 5-B：仅 admin 有 system:rbac_manage → 看得到「权限管理」tab
  const canRbacManage = useHasPermission('system:rbac_manage');
  // 薪酬阶段1（评审#5/#6）：薪酬配置仅 admin（system:salary_manage，与后端写端点同源），director 不可见/不可改
  const canSalaryManage = useHasPermission('system:salary_manage');

  const items: { key: ConfigGroup; label: string; icon: string; show: boolean }[] = [
    { key: 'ai',       label: 'AI 设置',     icon: '🤖', show: true },
    { key: 'business', label: '业务规则',    icon: '📋', show: true },
    { key: 'finance',  label: '财务规则',    icon: '💰', show: true },
    { key: 'data',     label: '数据 & 通知', icon: '📊', show: true },
    { key: 'code',     label: '客户编号',    icon: '🏷️', show: true },
    { key: 'wework',   label: '企微会话存档', icon: '💬', show: true }, // v1.2-rc2 ⑥
    { key: 'rbac',     label: '权限管理',    icon: '🔐', show: canRbacManage }, // v1.5 5-B
    { key: 'sms',      label: '短信配置',    icon: '📱', show: canRbacManage }, // v1.5 SMS UI
    { key: 'auth_2fa', label: '2FA 设置',    icon: '🔐', show: canRbacManage }, // v1.5 2FA UI
    { key: 'monitor',  label: '系统监控',    icon: '📊', show: canRbacManage }, // v1.5 P0 B6
    { key: 'fadada',   label: '电子签',      icon: '✍️', show: canRbacManage }, // B1-cfg 法大大
    { key: 'salary',   label: '薪酬配置',    icon: '💴', show: canSalaryManage }, // 薪酬阶段1（仅 admin）
    { key: 'test',     label: '测试中心',    icon: '🧪', show: IS_DEV },
  ];

  return (
    <>
      <div className="work-content">
        {/* P5 飞书风格 hero banner */}
        <div style={{
          padding: '28px 32px', marginBottom: 24,
          background: 'linear-gradient(135deg, #2C5BFF 0%, #6E5CFF 100%)',
          borderRadius: 12, color: '#FFFFFF',
          boxShadow: '0 4px 16px rgba(44, 91, 255, 0.15)',
          minHeight: 130,
        }}>
          <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.4, marginBottom: 6 }}>
            ⚙️ 管理控制台
          </div>
          <div style={{ fontSize: 14, opacity: 0.88, marginBottom: 14, lineHeight: 1.6 }}>
            统一管理 AI / 业务规则 / 财务 / 通知 / 测试
          </div>
          {latestUpdate && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 12px', borderRadius: 16,
              background: 'rgba(255,255,255,0.18)', fontSize: 12,
            }}>
              <span>🕐 最近修改：</span>
              <strong>{findLabel(latestUpdate.key)?.title ?? latestUpdate.key}</strong>
              <span style={{ opacity: 0.8 }}>· {fmtDateTime(latestUpdate.updated_at)}</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 24 }}>
          {/* 左侧二级导航 */}
          <aside style={{
            width: 200, flexShrink: 0, padding: 8,
            background: '#FFFFFF', borderRadius: 8, border: '1px solid #DEE0E3',
            height: 'fit-content',
          }}>
            {items.filter((i) => i.show).map((i) => (
              <div key={i.key}
                   onClick={() => setTab(i.key)}
                   style={{
                     padding: '10px 14px', marginBottom: 4, borderRadius: 6, cursor: 'pointer',
                     fontSize: 14, lineHeight: 1.6,
                     background: tab === i.key ? '#EFF2FE' : 'transparent',
                     color: tab === i.key ? '#2C5BFF' : '#1F2329',
                     fontWeight: tab === i.key ? 500 : 400,
                   }}>
                <span style={{ marginRight: 8 }}>{i.icon}</span>
                {i.label}
              </div>
            ))}
          </aside>

          {/* 右侧内容 */}
          <main style={{ flex: 1, minWidth: 0 }}>
            <GroupHeader group={tab} />
            {tab === 'ai' && <AiTab configs={aiConfigs ?? []} valueMap={valueMap} />}
            {tab === 'business' && <ConfigGrid group="business" valueMap={valueMap} />}
            {tab === 'finance' && <ConfigGrid group="finance" valueMap={valueMap} />}
            {tab === 'data' && <ConfigGrid group="data" valueMap={valueMap} />}
            {tab === 'code' && <ConfigGrid group="code" valueMap={valueMap} />}
            {tab === 'wework' && <WeworkArchivePage embedded />}
            {tab === 'rbac' && canRbacManage && <RbacAdmin />}
            {tab === 'sms' && canRbacManage && <SmsConfig />}
            {tab === 'auth_2fa' && canRbacManage && <Auth2faConfig />}
            {tab === 'monitor' && canRbacManage && <SystemMonitor />}
            {tab === 'fadada' && canRbacManage && <FadadaConfig />}
            {tab === 'salary' && canSalaryManage && <SalaryConfig />}
            {tab === 'test' && IS_DEV && <TestCenterEmbed />}
          </main>
        </div>
      </div>
    </>
  );
}

function GroupHeader({ group }: { group: ConfigGroup }) {
  // v1.2-rc2 ③：未知 tab（如 ?tab=rules）会让 GROUP_META[group] = undefined
  // 之前 meta.icon 直接 throw → director 进 /admin/control-panel?tab=rules 整页白屏
  const meta = GROUP_META[group] ?? {
    icon: '⚙️',
    label: group ? String(group) : '配置',
    desc: '未知分组（请从左侧重新选择）',
  } as any;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 20, fontWeight: 500, color: '#1F2329' }}>
        {meta?.icon ?? '⚙️'} {meta?.label ?? group}
      </div>
      <div style={{ fontSize: 12, color: '#646A73', marginTop: 4 }}>{meta?.desc ?? ''}</div>
    </div>
  );
}

/** ─── 卡片网格 ─── */
function ConfigGrid({ group, valueMap }: { group: ConfigGroup; valueMap: Map<string, string> }) {
  const labels = ADMIN_CONFIG_LABELS.filter((l) => l.group === group);
  if (labels.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#8F959E' }}>暂无配置项</div>;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
      {labels.map((l) => (
        <ConfigCard key={l.key} label={l} value={valueMap.get(l.key) ?? ''} />
      ))}
    </div>
  );
}

/** ─── 单个配置卡 ─── */
function ConfigCard({ label, value }: { label: ConfigLabel; value: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // v1.5 P0-2 二次调整：业务规则编辑按钮仅 admin 显示（director 可看不可改）
  // - lifecycle source（业务规则） → 用 system:lifecycle_manage 控制（仅 admin）
  // - system source（其它 system_configs）→ 用 system:configs_manage（admin+director）
  const canEditLifecycle = useHasPermission('system:lifecycle_manage');
  const canEditSystem = useHasPermission('system:configs_manage');
  const canEdit = label.source === 'lifecycle' ? canEditLifecycle : canEditSystem;

  useEffect(() => setDraft(value), [value]);

  const save = useMutation({
    mutationFn: () => {
      if (label.source === 'lifecycle') {
        return $.patch(`/lifecycle-config/${encodeURIComponent(label.key)}`, { value: draft });
      }
      return $.patch(`/admin/system-configs/${encodeURIComponent(label.key)}`, { value: draft });
    },
    onSuccess: () => {
      message.success(`${label.title} 已更新`);
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['lifecycle-config'] });
      qc.invalidateQueries({ queryKey: ['system-configs'] });
    },
    onError: (e: any) => message.error(e?.message ?? '保存失败'),
  });

  const isMissing = !value && value !== '0';

  return (
    <div style={{
      padding: 24, background: '#FFFFFF', borderRadius: 8, border: '1px solid #DEE0E3',
      transition: 'box-shadow 0.15s', lineHeight: 1.6,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 18 }}>{label.icon}</span>
        <span style={{ fontSize: 14, fontWeight: 500, color: '#1F2329' }}>{label.title}</span>
        {isMissing && <Pill tone="warning">未配置</Pill>}
      </div>
      <div style={{ fontSize: 12, color: '#646A73', marginBottom: 12 }}>{label.description}</div>

      {!editing ? (
        <>
          <div style={{
            padding: '8px 12px', background: '#F5F6F7', borderRadius: 6, marginBottom: 8,
            fontFamily: 'monospace', fontSize: 13, color: '#1F2329',
            wordBreak: 'break-all',
          }}>
            {value || <span style={{ color: '#8F959E' }}>（空）</span>}
            {label.unit && <span style={{ color: '#646A73', marginLeft: 6 }}>{label.unit}</span>}
          </div>
          {label.preview && value && (
            <div style={{ fontSize: 11, color: '#8F959E', marginBottom: 8 }}>
              预览：<span style={{ fontFamily: 'monospace' }}>{label.preview(value)}</span>
            </div>
          )}
          {canEdit
            ? <Btn kind="default" onClick={() => setEditing(true)}>✏️ 编辑</Btn>
            : <span style={{ fontSize: 12, color: '#8F959E' }}>🔒 仅 admin 可改</span>}
        </>
      ) : (
        <>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={{ marginBottom: 8 }}
            autoFocus
            suffix={label.unit ?? null}
          />
          {label.preview && (
            <div style={{ fontSize: 11, color: '#8F959E', marginBottom: 8 }}>
              预览：<span style={{ fontFamily: 'monospace' }}>{label.preview(draft)}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn kind="primary" onClick={() => save.mutate()} disabled={save.isPending || draft === value}>
              {save.isPending ? '保存中…' : '保存'}
            </Btn>
            <Btn kind="default" onClick={() => { setDraft(value); setEditing(false); }}>取消</Btn>
          </div>
        </>
      )}
    </div>
  );
}

/** ─── AI tab: 含 AI 月度预算 + 各 feature 开关 ─── */
function AiTab({ configs, valueMap }: { configs: any[]; valueMap: Map<string, string> }) {
  const aiLabels = ADMIN_CONFIG_LABELS.filter((l) => l.group === 'ai');
  return (
    <>
      {/* P5: Provider 凭证管理（顶部新增） */}
      <AiProvidersSection />

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: '#1F2329' }}>预算与开关</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {aiLabels.map((l) => (
            <ConfigCard key={l.key} label={l} value={valueMap.get(l.key) ?? ''} />
          ))}
        </div>
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, color: '#1F2329' }}>
          各功能 AI 配置（{configs.length}）
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {configs.map((c: any) => (
            <div key={c.id} style={{
              padding: 20, background: '#FFFFFF', borderRadius: 8, border: '1px solid #DEE0E3',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{c.featureName}</span>
                <Pill tone={c.enabled ? 'success' : 'default'}>{c.enabled ? '已启用' : '已停用'}</Pill>
              </div>
              <div style={{ fontSize: 11, color: '#8F959E', fontFamily: 'monospace', marginBottom: 6 }}>
                {c.featureKey}
              </div>
              <div style={{ fontSize: 12, color: '#646A73', marginBottom: 6 }}>{c.description}</div>
              <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                <span>模型：<strong>{c.provider}/{c.model}</strong></span>
                <span>月预算：<strong>¥{c.monthlyBudgetCny ?? '—'}</strong></span>
                <span>已花：<strong>¥{c.currentMonthSpent ?? 0}</strong></span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/** ─── 测试中心嵌入（仅 DEV） ─── */
function TestCenterEmbed() {
  return (
    <div style={{
      padding: 24, background: '#FFFBEA', border: '1px solid #FFC600', borderRadius: 8,
    }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#1F2329' }}>
        🧪 测试中心入口
      </div>
      <div style={{ fontSize: 12, color: '#646A73', marginBottom: 12 }}>
        手动触发 cron / 事件 — 仅开发环境可见，生产部署自动隐藏。
      </div>
      <Btn kind="primary" onClick={() => { window.location.href = '/admin/test-center'; }}>
        进入测试中心 →
      </Btn>
    </div>
  );
}
